import { getApiBaseUrl } from '../../utils/environment.mjs';
// perdiemService.mjs
import axios from 'axios';
import { secureLogger } from '../../utils/secureLogger.mjs';
import { startOfDay, inclusiveDayCount, formatLocalDate } from '../../utils/dateOnly.mjs';

const API_URL = getApiBaseUrl();

export const PerDiemService = {
  /**
   * File a per diem claim for someone in the admin's region. Takes the same
   * formatted payload as submitPerDiemClaim; the server prices it from the
   * published rates exactly as it does a claimant's own.
   */
  submitOnBehalf: async (userId, formattedData) => {
    try {
      const response = await axios.post(
        `${API_URL}/admin/claims/perdiem/on-behalf`,
        { ...formattedData, userId },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to file the claim');
    }
  },

  // Search for cities based on input query
  searchCities: async (query, country = null) => {
    if (query.length < 2) return [];
    
    try {
      const params = { query };
      if (country && country.trim()) {
        params.country = country.trim();
      }
      
      const response = await axios.get(`${API_URL}/cities/search`, {
        params,
        withCredentials: true, 
        headers: {
          'Content-Type': 'application/json'
        }
      });

      secureLogger.debug("City search results", response.data)
    
      return response.data; 
    } catch (error) {
      secureLogger.error('Error searching cities:', error.message);
      return [];
    }
  },
  // search for countries
  searchCountries: async (query) => {
    if (query.length < 2) return [];
    
    try {
      const response = await axios.get(`${API_URL}/countries/search`, {
        params: { query },
        withCredentials: true, 
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      return response.data; 
    } catch (error) {
      console.error(' Error searching countries:', error.response?.status, error.message);
      return [];
    }
  },

  /**
   * Get per diem rates for a city on a given date.
   *
   * The date is sent as a 'YYYY-MM-DD' CALENDAR date built from local
   * components. Passing a Date let axios serialise it via toISOString(), and
   * local midnight anywhere ahead of UTC (Nairobi +3, Mumbai +5:30) is the
   * PREVIOUS day in UTC - so 1 April was looked up as 31 March and priced at the
   * old season's rate, correcting itself only on the 2nd. Normalised here rather
   * than at each call site so no caller can reintroduce it.
   */
  getPerDiemRates: async (date=null, city, country) => {
    try {
      const dayOnly = date ? (formatLocalDate(date) || undefined) : undefined;
      const response = await axios.get(`${API_URL}/perdiem/rates`, {
        params: { date: dayOnly, city, country },
        withCredentials: true, 
        headers: {
          'Content-Type': 'application/json'
        }
      },
     );
      
      return response.data; 
    } catch (error) {
      console.error('Error getting per diem rates:', error);
      // Return default values on error
      return { 
        mie: 0, 
        breakfast: 0, 
        lunch: 0, 
        dinner: 0, 
        incidentals: 0, 
        lodging: 0,
        message: `Error getting rates for ${city}, ${country}: ${error.message}`
      };
    }
  },

  // Get all available countries
  getAvailableCountries: async () => {
    try {
      const response = await axios.get(`${API_URL}/countries`,  {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting countries:', error);
      return [];
    }
  },

  // Get cities for a specific country
  getCitiesForCountry: async (country) => {
    try {
      const response = await axios.get(`${API_URL}/cities/by-country/${encodeURIComponent(country)}`,
     {
        withCredentials: true, 
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error getting cities for ${country}:`, error);
      return [];
    }
  },


  // Save form data
  submitPerDiemClaim: async (claimData, isDraft = false) => {
    // Create payload that matches backend expectations
    const payload = {
      ...claimData,
      status: isDraft ? 'draft' : 'submitted'
    };
    
    try {
      const response = await axios.post(`${API_URL}/claims/per-diem`, payload, {
        withCredentials: true, 
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Return just the data and status for easier consumption
      return {
        success: true,
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error('Form submission error details:', error.response || error);
      throw new Error(error.response?.data?.message || 'Failed to save form');
    }
  },
  
  /**
   * Format form data to match backend expectations
   * @param {Object} formData - The raw form data
   * @returns {Object} - Formatted data for backend
   */
  formatClaimData: (formData) => {
    const { businessPurpose, startDate, endDate, lineItems } = formData;
    
    // Map form data to match backend structure
    const dailyDetails = lineItems.map(item => ({
      // The stored row's id, when it has one. The admin save matches days by id
      // first and date second, so without it correcting a day's DATE made it a
      // new day - and dropped the PTAE it was funded from. The claimant save
      // paths take a field allowlist, so the id is ignored there.
      ...(item.id && !String(item.id).startsWith('item-') ? { id: item.id } : {}),
      // Send the CALENDAR day as YYYY-MM-DD, not a Date.
      //
      // A Date JSON-serializes via toISOString(), i.e. UTC. A row picked as
      // 15 April sits at LOCAL midnight, so east of UTC it went over the wire as
      // "2026-04-14T21:00:00.000Z" and the server read the 14th. Rows loaded
      // from the server were UTC midnight and read back as the 15th - so two
      // lines for the same day produced two different day keys and the
      // duplicate-day check passed a clashing claim straight through.
      // A date-only string means the same day in every timezone.
      date: formatLocalDate(item.date),
      city: item.city || '',
      country: item.country || '',
      perDiemRate: item.stateDeptMIE || 0, // Map stateDeptMIE to perDiemRate
      breakfast: Number(item.breakfast) || 0,
      lunch: Number(item.lunch) || 0,
      dinner: Number(item.dinner) || 0,
      incidentals: Number(item.incidentals) || 0,
      totalMIE: Number(item.totalMIE) || 0,
      lodgingCost: Number(item.lodgingCost) || 0,
      runningTotal: Number(item.runningTotal) || 0,
      isFirstOrLastDay: PerDiemService.isFirstOrLastDay(item.date, startDate, endDate),
      // The day's 75% box. The server prices from this flag - the 75% figures
      // above are pulled back to the full rate without it - on the claimant's
      // save and the admin's alike.
      mieAt75Percent: item.mieAt75Percent === true,
    }));
    
    return {
      reasonForTrip: businessPurpose, // Map businessPurpose to reasonForTrip
      // Date-only strings, for the same reason as the line items above.
      //
      // These were sent as raw Date objects, which JSON.stringify turns into a
      // UTC timestamp. The form holds LOCAL midnight, so east of UTC a trip
      // entered as 31 Aug went over the wire as "2026-08-30T21:00:00.000Z" and
      // the DATEONLY column stored 2026-08-30 - the travel period lost a day on
      // the admin screen and in the email while the line items (already fixed)
      // stayed correct. That mismatch is exactly what made the bug so odd.
      startDate: formatLocalDate(startDate),
      endDate: formatLocalDate(endDate),
      dailyDetails: dailyDetails
    };
  },
  
  /**
   * Check if a date is the first or last day of the trip
   * @param {Date} date - The date to check
   * @param {Date} startDate - Trip start date
   * @param {Date} endDate - Trip end date
   * @returns {boolean} - Whether it's first or last day
   */
  isFirstOrLastDay: (date, startDate, endDate) => {
    // Local-midnight comparison so a calendar date matches in every timezone.
    const d = startOfDay(date);
    const start = startOfDay(startDate);
    const end = startOfDay(endDate);
    if (!d || !start || !end) return false;

    return d.getTime() === start.getTime() || d.getTime() === end.getTime();
  },
  
  /**
   * Calculate the expected number of days between dates
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {number} - Number of days
   */
  calculateExpectedDayCount: (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    // Inclusive, DST-safe day count on local midnights.
    return inclusiveDayCount(startDate, endDate);
  },
  
  // Get saved claims (drafts and submitted)
  getSavedClaims: async () => {
    try {
      const response = await axios.get(`${API_URL}/perdiem/claims`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching saved claims:', error);
      return {
        success: false,
        message: 'Failed to fetch claims',
        data: []
      };
    }
  },

  deletePerDiemClaimService: async (claimId) =>{
    try {
      
      const response = await axios.delete(`${API_URL}/perdiem/claims/${claimId}`,
        {
          withCredentials: true
        }
      );
      return response
    } catch (error) {
      console.error(`Error fetching claim #${claimId}:`, error);
      return {
        success: false,
        message: 'Failed to fetch claim details',
        data: null
      };
    }


  },

  
  // Get a specific claim by ID
  getPerDiemClaimByID: async (claimId) => {
    try {
      const response = await axios.get(`${API_URL}/perdiem/claims/${claimId}`,
        {
          withCredentials: true
        }
      );
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error(`Error fetching claim #${claimId}:`, error);
      return {
        success: false,
        message: 'Failed to fetch claim details',
        data: null
      };
    }
  },

  updatePerDiemClaim: async (claimId, formData, isDraft = false) => {
    try {
      const response = await axios.put(
        `${API_URL}/perdiem/claims/${claimId}`,
        {
          
          ...formData,
          isDraft,
          status: isDraft ? 'draft' : 'submitted'
        },
        {
          
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data; 
    } catch (error) {
      console.error('Error updating per diem claim:', error);
      throw error;
    }
  },


  adminUpdatePerDiemClaim: async (claimId, claimData, isDraft = false) => {
    try {
      const response = await axios.put(
        `${API_URL}/admin/claims/perdiem/${claimId}/update`,
        {
          ...claimData,
          status: isDraft ? 'draft' : 'submitted',
          isAdminEdit: true,
        },
        {
          withCredentials: true
        }
      );
      return response;
    } catch (error) {
      console.error('Error updating per diem claim as admin:', error);
      throw new Error(error.response?.data?.message || 'Failed to update claim');
    }
  },

  
  // Admin pulls back their own approval before the auditor picks the claim up.
  withdrawApproval: async (claimId, comments = '') => {
    const response = await axios.post(
      `${API_URL}/admin/claims/perdiem/${claimId}/withdraw`,
      { comments },
      { withCredentials: true }
    );
    return response.data;
  },
  submitAdminAction: async (claimId, actionData) => {
    try {
      // Check if there are documents to upload with the approval
      let documentsData = {};
      if (actionData.action === 'approved' && actionData.documents) {
        // Handle document upload separately if files are included
        const formData = new FormData();
        Array.from(actionData.documents).forEach(file => {
          formData.append('documents', file);
        });
        
        try {
          const documentResponse = await axios.post(
            `${API_URL}/perdiem/claims/${claimId}/documents`,
            formData,
            {
              withCredentials: true,
              // Don't set Content-Type header - axios will set it with the boundary
            }
          );
          
          documentsData = {
            documentsUploaded: true,
            documents: documentResponse.data.documents || []
          };
        } catch (error) {
          console.error('Document upload error:', error);
          // Continue with approval even if document upload fails
          documentsData = {
            documentsUploaded: false,
            error: error.message || 'Failed to upload documents'
          };
        }
      }
      
      // Now submit the admin action
      const response = await axios.post(
        `${API_URL}/admin/claims/${claimId}/perdiem/action`,
        {
          ...actionData,
          documentsData // Include information about documents
        },
        {
          withCredentials: true, 
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Admin action submission error:', error);
      throw error;
    }
  },

  /**
   * Upload supporting documents for a claim
   * @param {string} claimId - The ID of the claim
   * @param {FormData} formData - FormData object containing files
   * @returns {Promise<Object>} - Response with uploaded document information
   */
  uploadDocuments: async (claimId, formData) => {
    try {
      const response = await axios.post(
        `${API_URL}/perdiem/claims/${claimId}/documents`, 
        formData,
        {
          withCredentials: true,
        }
      );
      
      return {
        success: true,
        message: 'Documents uploaded successfully',
        documents: response.data.documents || []
      };
    } catch (error) {
      console.error('Error uploading documents:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload documents',
        documents: []
      };
    }
  },

  /**
   * Get all supporting documents for a claim
   * @param {string} claimId - The ID of the claim
   * @returns {Promise<Object>} - Response with document information
   */
  getDocuments: async (claimId) => {
    try {
      const response = await axios.get(
        `${API_URL}/perdiem/claims/${claimId}/documents`,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        success: true,
        documents: response.data.documents || []
      };
    } catch (error) {
      console.error('Error fetching documents:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch documents',
        documents: []
      };
    }
  },

  /**
   * Delete a specific document from a claim
   * @param {string} claimId - The ID of the claim
   * @param {string} documentId - The ID of the document to delete
   * @returns {Promise<Object>} - Response with operation status
   */
 
  async deleteDocument(claimId, documentId) {
    try {
      const response = await axios.delete(`${API_URL}/perdiem/claims/${claimId}/documents/${documentId}`, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Service error deleting document:', error);
      
      // Handle axios error response
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.message || `HTTP error! status: ${error.response.status}`;
        throw new Error(errorMessage);
      } else if (error.request) {
        // Request was made but no response received
        throw new Error('No response received from server');
      } else {
        // Something else happened
        throw new Error(error.message || 'Unknown error occurred');
      }
    }
  },

}

